import type { DiretoriaIndicator } from '@/data/diretoriaTypes';

export interface DiretoriaFaturamentoBrutoPayload {
  ytdTotal: number;
  mesAtual: number;
  variacaoPct: number | null;
  mensal: Array<{ mes: number; name: string; totalPago: number }>;
  porEmpresa: Array<{ nomeEmpresa: string; totalPago: number }>;
  querySql?: string;
}

export interface DiretoriaInadimplenciaPayload {
  saldoVencido: number;
  titulosVencidos: number;
  novosMes: number;
  faixas: Array<{ faixa: string; qtd: number; saldo: number }>;
  querySql?: string;
}

export interface DiretoriaInfracoesPayload {
  pagasMes: { qtd: number; valor: number };
  vencidas: { qtd: number; valor: number };
  aVencer: { qtd: number; valor: number };
  querySql?: string;
}

export interface DiretoriaMotosAlugadasPayload {
  ativas: number;
  novasMes: number;
  canceladasMes: number;
  mensal: Array<{ name: string; novas: number; canceladas: number }>;
  querySql?: string;
}

export interface DiretoriaRentabilidadePayload {
  receitaTotal: number;
  custoTotal: number;
  motosBase: number;
  mediaReceitaPorMoto: number;
  mediaCustoPorMoto: number;
  margemPct: number | null;
  mensal: Array<{ name: string; receita: number; custo: number }>;
  querySql?: string;
}

export interface DiretoriaLocacaoLocalPayload {
  totalLocacoes: number;
  cidadesDistintas: number;
  topCidade: string | null;
  contratosMes: number;
  itens: Array<{ bairro: string; cidade: string; quantidade: number }>;
  querySql?: string;
}

const fmtPct = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1).replace('.', ',')}%`;
};

const fmtMi = (value: number, formatCurrency: (n: number) => string): string => {
  if (Math.abs(value) >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(2).replace('.', ',')} mi`;
  }
  return formatCurrency(value);
};

const fmtQtdValor = (qtd: number, valor: number, formatCurrency: (n: number) => string): string =>
  `${qtd} · ${formatCurrency(valor)}`;

export function buildFaturamentoBrutoIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaFaturamentoBrutoPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  return {
    ...base,
    shortDescription: 'Faturamento bruto (baixas a receber) — ERP FlyStart.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Anual (YTD)',
        value: fmtMi(payload.ytdTotal, formatCurrency),
        hint: 'ERP FlyStart',
        tone: payload.ytdTotal > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Mês atual',
        value: formatCurrency(payload.mesAtual),
        hint: 'ERP FlyStart',
        tone: payload.mesAtual > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Var. vs mês ant.',
        value: fmtPct(payload.variacaoPct),
        hint: 'ERP FlyStart',
        tone: payload.variacaoPct == null ? 'neutral' : payload.variacaoPct >= 0 ? 'good' : 'bad',
      },
    ],
    chartKind: 'bar',
    series: payload.mensal.map((row) => ({ name: row.name, valor: Math.round(row.totalPago) })),
    barSeriesKey: 'valor',
    tableHeaders: ['Unidade', 'Faturamento mês'],
    tableRows: payload.porEmpresa.slice(0, 20).map((row) => [row.nomeEmpresa, formatCurrency(row.totalPago)]),
    footnote: 'Receitas realizadas (contabaixa, tipocontaid=1) agregadas por mês e por empresa no mês corrente.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildInadimplenciaIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaInadimplenciaPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  return {
    ...base,
    shortDescription: 'Contas a receber vencidas com saldo em aberto (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Saldo vencido',
        value: formatCurrency(payload.saldoVencido),
        hint: 'ERP FlyStart',
        tone: payload.saldoVencido > 0 ? 'bad' : 'good',
      },
      {
        label: 'Títulos vencidos',
        value: String(payload.titulosVencidos),
        hint: 'ERP FlyStart',
        tone: payload.titulosVencidos > 0 ? 'bad' : 'good',
      },
      {
        label: 'Novos no mês',
        value: String(payload.novosMes),
        hint: 'Vencimento no mês corrente',
        tone: payload.novosMes > 0 ? 'bad' : 'good',
      },
    ],
    chartKind: 'bar',
    series: payload.faixas.map((row) => ({ name: row.faixa, valor: row.qtd, valor2: Math.round(row.saldo) })),
    barSeriesKey: 'valor',
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    tableRows: payload.faixas.map((row) => [row.faixa, String(row.qtd), formatCurrency(row.saldo)]),
    footnote: 'Exclui multas/infrações; saldo = totalliquido − baixas. Aging calculado sobre data_vencimento.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildInfracoesIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaInfracoesPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  return {
    ...base,
    shortDescription: 'Multas e infrações classificadas por descrição/observação (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Pagas (mês)',
        value: fmtQtdValor(payload.pagasMes.qtd, payload.pagasMes.valor, formatCurrency),
        hint: 'ERP FlyStart',
        tone: 'good',
      },
      {
        label: 'Vencidas',
        value: fmtQtdValor(payload.vencidas.qtd, payload.vencidas.valor, formatCurrency),
        hint: 'ERP FlyStart',
        tone: payload.vencidas.qtd > 0 ? 'bad' : 'good',
      },
      {
        label: 'A vencer',
        value: fmtQtdValor(payload.aVencer.qtd, payload.aVencer.valor, formatCurrency),
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
    ],
    chartKind: 'line',
    series: [
      { name: 'Pagas', valor: payload.pagasMes.qtd, valor2: Math.round(payload.pagasMes.valor) },
      { name: 'Vencidas', valor: payload.vencidas.qtd, valor2: Math.round(payload.vencidas.valor) },
      { name: 'A vencer', valor: payload.aVencer.qtd, valor2: Math.round(payload.aVencer.valor) },
    ],
    tableHeaders: ['Estado', 'Qtd.', 'Valor'],
    tableRows: [
      ['Pagas (mês)', String(payload.pagasMes.qtd), formatCurrency(payload.pagasMes.valor)],
      ['Vencidas', String(payload.vencidas.qtd), formatCurrency(payload.vencidas.valor)],
      ['A vencer', String(payload.aVencer.qtd), formatCurrency(payload.aVencer.valor)],
    ],
    footnote: 'Heurística alinhada ao app mobile: multa, infração, auto, recebimento multa.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildMotosAlugadasIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaMotosAlugadasPayload
): DiretoriaIndicator {
  return {
    ...base,
    shortDescription: 'Frota alugada e fluxo de locações (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Ativas (est.)',
        value: String(payload.ativas),
        hint: 'status_equipamento=alugada',
        tone: payload.ativas > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Novas no mês',
        value: String(payload.novasMes),
        hint: 'Início de contrato',
        tone: payload.novasMes > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Canceladas no mês',
        value: String(payload.canceladasMes),
        hint: 'data_cancelamento',
        tone: 'neutral',
      },
    ],
    chartKind: 'bar',
    series: payload.mensal.map((row) => ({ name: row.name, valor: row.novas, valor2: row.canceladas })),
    barSeriesKey: 'valor',
    tableHeaders: ['Mês', 'Entradas', 'Saídas'],
    tableRows: payload.mensal.slice(-2).map((row) => [row.name, String(row.novas), String(row.canceladas)]),
    footnote: 'Ativas pela frota (equipamentos); entradas/saídas pela tabela locacao (início e cancelamento).',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildRentabilidadeIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaRentabilidadePayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  return {
    ...base,
    shortDescription: 'Receita realizada vs custo atual por moto (ERP FlyStart).',
    primaryKpiIndex: 2,
    kpis: [
      {
        label: 'Média receita / moto',
        value: `${formatCurrency(payload.mediaReceitaPorMoto)} / moto`,
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: 'Média custo / moto',
        value: `${formatCurrency(payload.mediaCustoPorMoto)} / moto`,
        hint: 'ERP FlyStart',
        tone: payload.mediaCustoPorMoto < 0 ? 'bad' : 'neutral',
      },
      {
        label: 'Margem',
        value: fmtPct(payload.margemPct),
        hint: 'Receita − |custo|',
        tone: payload.margemPct == null ? 'neutral' : payload.margemPct >= 15 ? 'good' : 'neutral',
      },
    ],
    chartKind: 'line',
    series: payload.mensal.map((row) => ({
      name: row.name,
      valor: Math.round(row.receita),
      valor2: Math.round(row.custo),
    })),
    tableHeaders: ['Componente', 'Valor'],
    tableRows: [
      ['Receita atribuída (YTD)', formatCurrency(payload.receitaTotal)],
      ['Custos atribuídos', formatCurrency(payload.custoTotal)],
      ['Motos (base)', String(payload.motosBase)],
    ],
    footnote: 'Receita por baixas vinculadas ao equipamento; custo = compra, OS e financeiro por moto.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildLocacaoLocalIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaLocacaoLocalPayload
): DiretoriaIndicator {
  const byCidade = new Map<string, number>();
  for (const item of payload.itens) {
    byCidade.set(item.cidade, (byCidade.get(item.cidade) ?? 0) + item.quantidade);
  }
  const chartRows = [...byCidade.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, valor]) => ({ name, valor }));

  return {
    ...base,
    shortDescription: 'Locações ativas agrupadas por bairro e cidade do locatário (ERP FlyStart).',
    primaryKpiIndex: 1,
    kpis: [
      {
        label: 'Cidades',
        value: String(payload.cidadesDistintas),
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: 'Top cidade',
        value: payload.topCidade ?? '—',
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: 'Contratos (mês)',
        value: String(payload.contratosMes),
        hint: 'Novas locações',
        tone: payload.contratosMes > 0 ? 'good' : 'neutral',
      },
    ],
    chartKind: 'bar',
    series: chartRows,
    barSeriesKey: 'valor',
    tableHeaders: ['Bairro', 'Cidade', 'Locações'],
    tableRows: payload.itens.slice(0, 30).map((row) => [row.bairro, row.cidade, String(row.quantidade)]),
    footnote: 'Join locacao + pessoa_crm (bairro/cidade); contratos ativos sem cancelamento.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}
